import { SchemaPerspective, SchemaStructuralGuidance, AssigneeDescriptor } from 'types/schema';
import { FilterExpression } from 'types/filters';

/**
 * Pure helper: resolves each perspective's `assignee_filter` into readable
 * valid-assignee descriptors. Mirrors the `type`/`label` semantics in
 * getValidAssignmentTypes.ts — `property: 'type'` compares against a schema
 * `system_name`, `property: 'label'` against a `node_label`.
 *
 * Operator handling (an assignee_filter is a UnifiedFilters FilterExpression,
 * so the payload location depends on the operator):
 *   - `equals` (and a legacy missing operator, which the evaluator defaults to
 *     `equals`) → the value is on `filter.value`.
 *   - `in`     → the values are on `filter.values[]`; expanded to one descriptor
 *     per entry. Seeded system schemas DO use this (e.g. center_orbit /
 *     center_calendar_rotation `center_responsible`:
 *     `{ property: 'label', operator: 'in', values: ['subject', 'role'] }`).
 *
 * Anything that cannot be faithfully expressed as a POSITIVE type/label
 * descriptor — a negated operator (`not_in`/`neq`), or a filter on some other
 * property — is deliberately NOT emitted as a valid assignee (rendering an
 * exclusion as an allowance would be inverted and dangerous). Instead the
 * perspective is marked `hasUndescribedConstraints`, so a consumer can never
 * mistake a partial description for the exhaustive allowed set.
 *
 * No cross-schema lookup.
 */
export function describeAssigneeFilters(
  perspectives: SchemaPerspective[]
): SchemaStructuralGuidance {
  return {
    perspectives: (perspectives ?? []).map((perspective) => {
      const filters = perspective.assignee_filter ?? [];

      const validAssignees: AssigneeDescriptor[] = [];
      let hasUndescribedConstraints = false;

      for (const filter of filters) {
        const descriptors = describeOne(filter);
        if (descriptors === null) {
          hasUndescribedConstraints = true;
          continue;
        }
        validAssignees.push(...descriptors);
      }

      return {
        perspectiveSystemName: perspective.system_name,
        validAssignees,
        unconstrained: filters.length === 0,
        ...(hasUndescribedConstraints ? { hasUndescribedConstraints: true } : {}),
      };
    }),
  };
}

/**
 * Expand one filter alternative into positive descriptors, or return `null`
 * when it cannot be faithfully described as a positive type/label allowance.
 */
function describeOne(filter: FilterExpression): AssigneeDescriptor[] | null {
  const property = filter?.property;
  if (property !== 'type' && property !== 'label') return null;

  // Legacy filters omitted the operator; the canonical evaluator defaults it to
  // 'equals' (see evaluateAssigneeFilter), so mirror that here.
  const operator = filter.operator ?? 'equals';

  if (operator === 'equals') {
    if (filter.value === undefined || filter.value === null) return null;
    return [{ property, value: String(filter.value) }];
  }

  if (operator === 'in') {
    if (!Array.isArray(filter.values) || filter.values.length === 0) return null;
    return filter.values.map((value) => ({ property, value: String(value) }));
  }

  // Negated or otherwise non-positive operator — describing it as an allowance
  // would invert its meaning.
  return null;
}
